import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { TextButton, useIsMounted, useOverlayContext, useStateInRef } from 'seagull';
import { useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import { cardIdentity } from '../utils/opportunityCard';
import { dismissalRequests, dismissedToastText } from '../utils/opportunityDismissals';
import { useDismissRevenueOpportunity } from './useDismissRevenueOpportunity';
import { useRevenueOpportunitiesSummary } from './useRevenueOpportunitiesSummary';
import { useUndoRevenueOpportunityDismissal } from './useUndoRevenueOpportunityDismissal';
const UNDO_BUTTON_LOOK = {
    weight: 'normal',
    underline: 'none',
    variant: 'paragraph1'
};
const UNDO_BI_NAME = 'pulse_opportunities_undo';
export const useRevenueOpportunityDismissals = (toastTestIds)=>{
    var _useStore;
    const { t: texts } = useLocaleKeys('ai.pulse');
    const { showInfoToast, showErrorToast } = useOverlayContext();
    const isMounted = useIsMounted();
    const businessId = (_useStore = useStore(selectors.user.businessId)) !== null && _useStore !== void 0 ? _useStore : '';
    const { summary, isFetching, readSummaryState } = useRevenueOpportunitiesSummary();
    const { dismissRevenueOpportunity } = useDismissRevenueOpportunity();
    const { undoRevenueOpportunityDismissal } = useUndoRevenueOpportunityDismissal();
    const [dismissingIdentities, setDismissingIdentities] = useState([]);
    const departures = useMemo(()=>new Set(), []);
    const getReleaseSettledDepartures = useStateInRef(releaseSettledDepartures);
    useEffect(()=>{
        getReleaseSettledDepartures()();
    }, [
        summary,
        isFetching
    ]);
    function releaseSettledDepartures() {
        var _ref;
        var _latest_summary;
        const latest = readSummaryState();
        const shownIdentities = ((_ref = (_latest_summary = latest.summary) === null || _latest_summary === void 0 ? void 0 : _latest_summary.cards) !== null && _ref !== void 0 ? _ref : []).map(cardIdentity);
        departures.forEach((departure)=>{
            if (!latest.isFetching || !shownIdentities.includes(departure.identity)) {
                departures.delete(departure);
                departure.release();
            }
        });
    }
    function waitUntilCardLeaves(identity) {
        return new Promise((release)=>{
            departures.add({
                identity,
                release
            });
            releaseSettledDepartures();
        });
    }
    async function undoDismissals(dismissalIds) {
        const failedCount = await undoRevenueOpportunityDismissal({
            dismissalIds,
            businessId
        });
        if (isMounted() && failedCount > 0) {
            showErrorToast(texts.section.toasts.undoFailed(), toastTestIds.UndoFailed());
        }
    }
    function undoAction(onUndo, biData) {
        const bi = {
            name: UNDO_BI_NAME,
            data: biData
        };
        return /*#__PURE__*/ _jsx(TextButton, _object_spread_props(_object_spread({}, UNDO_BUTTON_LOOK), {
            bi: bi,
            testId: toastTestIds.Undo(),
            onClick: onUndo,
            children: texts.section.toasts.undo()
        }));
    }
    function showDismissedToast(option, undoIds, biData) {
        const undo = ()=>{
            closeToast();
            void undoDismissals(undoIds);
        };
        const closeToast = showInfoToast(dismissedToastText(option, texts), toastTestIds.Dismissed(), {
            action: undoIds.length > 0 ? undoAction(undo, biData) : undefined
        });
    }
    function reportDismissal(option, settled, biData) {
        const { undoIds, dismissedCount, failedCount } = settled;
        if (failedCount > 0) {
            showErrorToast(texts.section.toasts.dismissFailed(), toastTestIds.DismissFailed());
        }
        if (dismissedCount > 0) {
            showDismissedToast(option, undoIds, biData);
        }
    }
    const dismissCard = async (card, identity, option, biData)=>{
        setDismissingIdentities((current)=>[
                ...current,
                identity
            ]);
        const settled = await dismissRevenueOpportunity({
            requests: dismissalRequests(card, option),
            businessId
        });
        if (settled.dismissedCount > 0) {
            await waitUntilCardLeaves(identity);
        }
        if (isMounted()) {
            setDismissingIdentities((current)=>current.filter((pending)=>pending !== identity));
            reportDismissal(option, settled, biData);
        }
    };
    return {
        isDismissing: (identity)=>dismissingIdentities.includes(identity),
        dismissCard
    };
};
